// app/src/lib/revenue/receiptArchive.js — receipt search + download (US-4.8).
//
// All scanned images are retained in S3 under `${userId}/${receiptId}.${ext}`
// (see Phase 1 receipts presign Lambda). For each Invoice we know:
//   - receiptObjectKey: the S3 key where the original was uploaded.
//
// Search runs entirely client-side against Dexie. The download path mints
// presigned GETs via a new `/receipts/download-urls` Lambda (added below).

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';

/** @typedef {{ from?:string, to?:string, vendorName?:string, minAmount?:number, maxAmount?:number, category?:string }} SearchQuery */

/** Run a search across the user's invoices. Returns rows with `receiptObjectKey` set. */
export async function search(q = {}) {
  const userId = await requireUserId();
  let rows = await db.invoices.where('userId').equals(userId).toArray();
  if (q.from) rows = rows.filter((r) => r.issuedDate >= q.from);
  if (q.to)   rows = rows.filter((r) => r.issuedDate <= q.to);
  if (q.minAmount != null) {
    const min = Math.round(q.minAmount * 100);
    rows = rows.filter((r) => (r.totalCents || 0) >= min);
  }
  if (q.maxAmount != null) {
    const max = Math.round(q.maxAmount * 100);
    rows = rows.filter((r) => (r.totalCents || 0) <= max);
  }
  if (q.vendorName) {
    const needle = q.vendorName.toLowerCase();
    // Resolve vendor names lazily for filtering.
    const vendors = await db.vendors.where('userId').equals(userId).toArray();
    const byId = new Map(vendors.map((v) => [v.vendorId, v]));
    rows = rows.filter((r) => {
      const v = byId.get(r.vendorId);
      return v && v.name.toLowerCase().includes(needle);
    });
  }
  if (q.category) {
    // Invoice doesn't carry category; intersect with line items' items' category.
    const cat = q.category;
    rows = await asyncFilter(rows, async (r) => {
      const lis = await db.lineItems.where('invoiceId').equals(r.invoiceId).toArray();
      if (!lis.length) return false;
      const itemIds = lis.map((l) => l.itemId).filter(Boolean);
      const items = itemIds.length ? await db.items.where('itemId').anyOf(itemIds).toArray() : [];
      return items.some((it) => it.category === cat);
    });
  }
  rows.sort((a, b) => b.issuedDate.localeCompare(a.issuedDate));
  return rows;
}

/**
 * Request presigned GET URLs for a set of receiptObjectKeys.
 * The server-side Lambda enforces that every key is owned by the caller.
 */
export async function downloadUrls(objectKeys) {
  if (!Array.isArray(objectKeys) || !objectKeys.length) return [];
  if (objectKeys.length > 200) throw new Error('downloadUrls: max 200 per request');
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('downloadUrls: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/receipts/download-urls`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ objectKeys }),
  });
  if (!res.ok) throw new Error(`downloadUrls: ${res.status}`);
  return (await res.json()).urls || [];
}

async function asyncFilter(arr, pred) {
  const keep = await Promise.all(arr.map(pred));
  return arr.filter((_, i) => keep[i]);
}
